import type { StateLookupOption } from 'src/api/services/policy.service';

import dayjs from 'dayjs';
import { memo, useRef, useState, useEffect, type Dispatch, type SetStateAction } from 'react';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { DatePicker } from '@mui/x-date-pickers';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Chip, Button, MenuItem, TextField, IconButton, Typography } from '@mui/material';

import type {
  NamesAndAddressesDraft,
  NamesAndAddressesNameDraft,
  NamesAndAddressesFieldErrors,
  NamesAndAddressesAddressDraft,
} from './names-and-addresses.types';

export type NamesAndAddressesEditProps = {
  draft: NamesAndAddressesDraft;
  setDraft: Dispatch<SetStateAction<NamesAndAddressesDraft>>;
  stateLookups: StateLookupOption[];
  fieldErrors: NamesAndAddressesFieldErrors;
};

type EditableDatePickerProps = {
  testId: string;
  label: string;
  value: string;
  error?: string;
  onValidChange: (value: dayjs.Dayjs | null) => void;
};

const formatEditableZipcode = (zipcode: string): string => {
  const digits = zipcode.replace(/\D/g, '').slice(0, 9);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};

const editFieldSx = {
  height: 54,
  '& .MuiInputBase-root': {
    height: '100%',
  },
  '& .MuiInputBase-input': {
    height: '100%',
    boxSizing: 'border-box',
  },
};

const editRowSx = {
  display: 'flex',
  gap: 2,
  alignItems: 'flex-start',
  width: '100%',
};

const editFieldColumnSx = {
  flex: 1,
  minWidth: 0,
};

// Memoized so editing one field doesn't re-render every other date field in the form on each keystroke.
const EditableDatePicker = memo(function EditableDatePickerInner({
  testId,
  label,
  value,
  error,
  onValidChange,
}: Readonly<EditableDatePickerProps>) {
  const [pickerValue, setPickerValue] = useState(() => (value ? dayjs(value, 'MM/DD/YYYY') : null));
  const [hasInputError, setHasInputError] = useState(false);

  useEffect(() => {
    if (!hasInputError) {
      setPickerValue(value ? dayjs(value, 'MM/DD/YYYY') : null);
    }
  }, [hasInputError, value]);

  return (
    <div data-testid={testId}>
      <DatePicker
        label={label}
        format="MM/DD/YYYY"
        value={pickerValue}
        onChange={(nextValue) => {
          setPickerValue(nextValue);
          if (nextValue === null || nextValue.isValid()) {
            setHasInputError(false);
            onValidChange(nextValue);
          }
        }}
        onError={(reason) => {
          setHasInputError(Boolean(reason));
          if (reason) onValidChange(null);
        }}
        slotProps={{
          textField: {
            fullWidth: true,
            size: 'medium',
            error: Boolean(error),
            helperText: error,
            sx: editFieldSx,
          },
        }}
      />
    </div>
  );
});

const updateName = (
  setDraft: Dispatch<SetStateAction<NamesAndAddressesDraft>>,
  rowKey: string,
  update: (name: NamesAndAddressesNameDraft) => NamesAndAddressesNameDraft
) => {
  setDraft((current) => ({
    ...current,
    additionalNames: current.additionalNames.map((name) =>
      name.rowKey === rowKey ? update(name) : name
    ),
  }));
};

const updateAdditionalAddress = (
  setDraft: Dispatch<SetStateAction<NamesAndAddressesDraft>>,
  nameRowKey: string,
  addressRowKey: string,
  changes: Partial<NamesAndAddressesAddressDraft>
): void => {
  updateName(setDraft, nameRowKey, (current) => ({
    ...current,
    addresses: current.addresses.map((item) =>
      item.rowKey === addressRowKey ? { ...item, ...changes } : item
    ),
  }));
};

const addAdditionalAddress = (
  setDraft: Dispatch<SetStateAction<NamesAndAddressesDraft>>,
  name: NamesAndAddressesNameDraft
): void =>
  updateName(setDraft, name.rowKey, (current) => ({
    ...current,
    addresses: [
      ...current.addresses,
      {
        rowKey: `address-new-${Date.now()}`,
        policyAddressId: null,
        addressId: null,
        street: '',
        city: '',
        stateCode: null,
        zipcode: '',
        email: '',
        effectiveDate: '',
        expirationDate: '',
      },
    ],
  }));

const deleteAdditionalName = (
  setDraft: Dispatch<SetStateAction<NamesAndAddressesDraft>>,
  name: NamesAndAddressesNameDraft
): void =>
  setDraft((current) => ({
    ...current,
    additionalNames: current.additionalNames.filter((item) => item.rowKey !== name.rowKey),
    deletedAdditionalNameIds: name.nameId
      ? [...current.deletedAdditionalNameIds, name.nameId]
      : current.deletedAdditionalNameIds,
  }));

const deleteAdditionalAddress = (
  setDraft: Dispatch<SetStateAction<NamesAndAddressesDraft>>,
  name: NamesAndAddressesNameDraft,
  address: NamesAndAddressesAddressDraft
): void =>
  setDraft((current) => ({
    ...current,
    additionalNames: current.additionalNames.map((item) =>
      item.rowKey === name.rowKey
        ? {
            ...item,
            addresses: item.addresses.filter((child) => child.rowKey !== address.rowKey),
          }
        : item
    ),
    deletedAdditionalAddressIds: address.policyAddressId
      ? [...current.deletedAdditionalAddressIds, address.policyAddressId]
      : current.deletedAdditionalAddressIds,
  }));

// Memoized so editing one address (of potentially several per name) doesn't re-render every other address/name block on each keystroke.
const AddressFields = memo(function AddressFieldsInner({
  address,
  addressNumber,
  fieldPrefix,
  stateLookups,
  showDates = true,
  onChange,
  onDelete,
  fieldErrors,
}: Readonly<{
  address: NamesAndAddressesAddressDraft;
  addressNumber?: number;
  fieldPrefix: string;
  stateLookups: StateLookupOption[];
  showDates?: boolean;
  onChange: (update: Partial<NamesAndAddressesAddressDraft>) => void;
  onDelete?: () => void;
  fieldErrors: NamesAndAddressesFieldErrors;
}>) {
  const field = (name: string) => fieldErrors[`${fieldPrefix}.${name}`];
  return (
    <Box sx={{ mt: 1, position: 'relative', overflow: 'visible' }}>
      {addressNumber !== undefined && (
        <Chip
          label={addressNumber}
          size="small"
          color="default"
          sx={{ position: 'absolute', top: -10, left: -10, zIndex: 3, height: 18, minWidth: 18 }}
        />
      )}
      <Box sx={editRowSx}>
        <Box sx={editFieldColumnSx}>
          <TextField
            fullWidth
            size="medium"
            label="Street"
            sx={editFieldSx}
            data-testid={`${fieldPrefix}-street-input`}
            value={address.street}
            onChange={(event) => onChange({ street: event.target.value })}
            error={Boolean(field('street'))}
            helperText={field('street')}
          />
        </Box>
        <Box sx={editFieldColumnSx}>
          <TextField
            fullWidth
            size="medium"
            label="City"
            sx={editFieldSx}
            data-testid={`${fieldPrefix}-city-input`}
            value={address.city}
            onChange={(event) => onChange({ city: event.target.value })}
            error={Boolean(field('city'))}
            helperText={field('city')}
          />
        </Box>
        <Box sx={editFieldColumnSx}>
          <TextField
            select
            fullWidth
            size="medium"
            label="State"
            sx={editFieldSx}
            data-testid={`${fieldPrefix}-state-select`}
            value={address.stateCode && address.stateCode > 0 ? address.stateCode : ''}
            onChange={(event) => onChange({ stateCode: Number(event.target.value) })}
            error={Boolean(field('stateCode'))}
            helperText={field('stateCode')}
          >
            {stateLookups.map((lookup) => (
              <MenuItem key={lookup.stateCode} value={lookup.stateCode}>
                {lookup.stateAbbreviation}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={editFieldColumnSx}>
          <TextField
            fullWidth
            size="medium"
            label="ZIP"
            sx={editFieldSx}
            data-testid={`${fieldPrefix}-zip-input`}
            value={formatEditableZipcode(address.zipcode)}
            onChange={(event) => onChange({ zipcode: event.target.value.replace(/\D/g, '').slice(0, 9) })}
            slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 10, pattern: '[0-9]*' } }}
            error={Boolean(field('zipcode'))}
            helperText={field('zipcode')}
          />
        </Box>
        {onDelete && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: 48, pt: 1 }}>
            <IconButton
              aria-label="Delete address"
              data-testid={`${fieldPrefix}-delete-address`}
              onClick={onDelete}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
      </Box>
      {showDates && (
        <Box sx={{ ...editRowSx, mt: 2 }}>
          <Box sx={editFieldColumnSx}>
            <EditableDatePicker
              testId={`${fieldPrefix}-effective-date`}
              label="Effective date"
              value={address.effectiveDate}
              error={field('effectiveDate')}
              onValidChange={(value) => onChange({ effectiveDate: value?.isValid() ? value.format('MM/DD/YYYY') : '' })}
            />
          </Box>
          <Box sx={editFieldColumnSx}>
            <EditableDatePicker
              testId={`${fieldPrefix}-expiration-date`}
              label="Expiration date"
              value={address.expirationDate}
              error={field('expirationDate')}
              onValidChange={(value) => onChange({ expirationDate: value?.isValid() ? value.format('MM/DD/YYYY') : '' })}
            />
          </Box>
          {onDelete && <Box aria-hidden="true" sx={{ minWidth: 48 }} />}
        </Box>
      )}
    </Box>
  );
});

// Memoized so editing Primary Employer doesn't re-render the Agency block (and vice versa) on each keystroke.
const NameFields = memo(function NameFieldsInner({
  name,
  prefix,
  required,
  setDraft,
  stateLookups,
  fieldErrors,
}: Readonly<{
  name: NamesAndAddressesNameDraft;
  prefix: string;
  required: boolean;
  setDraft: Dispatch<SetStateAction<NamesAndAddressesDraft>>;
  stateLookups: StateLookupOption[];
  fieldErrors: NamesAndAddressesFieldErrors;
}>) {
  const field = (fieldName: string) => fieldErrors[`${prefix}.${fieldName}`];
  const update = (changes: Partial<NamesAndAddressesNameDraft>) =>
    setDraft((current) => ({
      ...current,
      [prefix === 'primaryEmployer' ? 'primaryEmployer' : 'agency']: { ...name, ...changes },
    }));
  return (
    <Box>
      <Box sx={editRowSx}>
        <Box sx={editFieldColumnSx}>
          <TextField
            fullWidth
            size="medium"
            label="Name"
            sx={editFieldSx}
            data-testid={`${prefix}-name-input`}
            value={name.name}
            onChange={(event) => update({ name: event.target.value })}
            slotProps={{ htmlInput: { maxLength: prefix === 'agency' ? 30 : 90 } }}
            error={Boolean(field('name'))}
            helperText={field('name')}
          />
        </Box>
        {prefix === 'primaryEmployer' && (
          <Box sx={editFieldColumnSx}>
            <TextField
              fullWidth
              size="medium"
              label="FEIN"
              sx={editFieldSx}
              data-testid={`${prefix}-fein-input`}
              value={name.fein}
              onChange={(event) => update({ fein: event.target.value })}
              error={Boolean(field('fein'))}
              helperText={field('fein')}
            />
          </Box>
        )}
        <Box sx={editFieldColumnSx}>
          <TextField
            fullWidth
            size="medium"
            label="Email address"
            sx={editFieldSx}
            data-testid={`${prefix}-email-input`}
            value={name.email}
            onChange={(event) => update({ email: event.target.value })}
            slotProps={{ htmlInput: { maxLength: 42 } }}
            error={Boolean(field('email'))}
            helperText={field('email')}
          />
        </Box>
      </Box>
      <Typography variant="subtitle2" sx={{ mt: 3 }}>
        Mailing address
      </Typography>
      {name.mailingAddress ? (
        <AddressFields
          address={name.mailingAddress}
          fieldPrefix={`${prefix}.mailingAddress`}
          stateLookups={stateLookups}
          showDates={false}
          fieldErrors={fieldErrors}
          onChange={(changes) =>
            update({ mailingAddress: { ...name.mailingAddress!, ...changes } })
          }
        />
      ) : (
        required && (
          <Typography color="error" variant="caption">
            Mailing address is required.
          </Typography>
        )
      )}
    </Box>
  );
});

// Memoized so editing one additional name/address block doesn't re-render every other block on each keystroke.
// Existing blocks default to a read-only summary (no DatePickers mounted) so switching into the tab
// with many additional names/addresses doesn't pay their mount cost until the user opts in per-row.
const AdditionalNameBlock = memo(function AdditionalNameBlockInner({
  name,
  index,
  setDraft,
  stateLookups,
  fieldErrors,
  defaultEditing,
}: Readonly<{
  name: NamesAndAddressesNameDraft;
  index: number;
  setDraft: Dispatch<SetStateAction<NamesAndAddressesDraft>>;
  stateLookups: StateLookupOption[];
  fieldErrors: NamesAndAddressesFieldErrors;
  defaultEditing: boolean;
}>) {
  const [isEditing, setIsEditing] = useState(defaultEditing);
  const rowHasErrors = Object.keys(fieldErrors).some((key) => key.startsWith(`additionalNames.${index}.`));
  const showEditForm = isEditing || rowHasErrors;

  if (!showEditForm) {
    const stateAbbreviation = (stateCode: number | null) =>
      stateLookups.find((lookup) => lookup.stateCode === stateCode)?.stateAbbreviation ?? '';

    return (
      <Box
        sx={{ mt: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}
        data-testid={`additional-name-edit-${index}`}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="subtitle1" data-testid={`names-and-addresses-additional-name-${index}-summary-name`}>
              {name.name || 'Unnamed additional employer'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              FEIN: {name.fein || 'N/A'} &middot; Effective: {name.effectiveDate || 'N/A'} &middot; Expiration:{' '}
              {name.expirationDate || 'N/A'}
            </Typography>
            {name.addresses.map((address) => (
              <Typography key={address.rowKey} variant="body2" color="text.secondary">
                {[address.street, address.city, stateAbbreviation(address.stateCode), address.zipcode]
                  .filter(Boolean)
                  .join(', ') || 'No address on file'}
              </Typography>
            ))}
          </Box>
          <IconButton
            aria-label="Edit additional name"
            data-testid={`names-and-addresses-additional-name-${index}-edit`}
            onClick={() => setIsEditing(true)}
          >
            <EditIcon />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{ mt: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}
      data-testid={`additional-name-edit-${index}`}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          size="small"
          disabled={rowHasErrors}
          data-testid={`names-and-addresses-additional-name-${index}-done`}
          onClick={() => setIsEditing(false)}
        >
          Done
        </Button>
      </Box>
      <Box sx={editRowSx}>
        <Box sx={editFieldColumnSx}>
          <TextField
            fullWidth
            size="medium"
            label="Employer name"
            sx={editFieldSx}
            data-testid={`names-and-addresses-additional-name-${index}-name-input`}
            value={name.name}
            onChange={(event) =>
              updateName(setDraft, name.rowKey, (current) => ({
                ...current,
                name: event.target.value,
              }))
            }
            slotProps={{ htmlInput: { maxLength: 90 } }}
            error={Boolean(fieldErrors[`additionalNames.${index}.name`])}
            helperText={fieldErrors[`additionalNames.${index}.name`]}
          />
        </Box>
        <Box sx={editFieldColumnSx}>
          <TextField
            fullWidth
            size="medium"
            label="FEIN"
            sx={editFieldSx}
            data-testid={`names-and-addresses-additional-name-${index}-fein-input`}
            value={name.fein}
            onChange={(event) =>
              updateName(setDraft, name.rowKey, (current) => ({
                ...current,
                fein: event.target.value,
              }))
            }
            error={Boolean(fieldErrors[`additionalNames.${index}.fein`])}
            helperText={fieldErrors[`additionalNames.${index}.fein`]}
          />
        </Box>
        <Box sx={editFieldColumnSx}>
          <EditableDatePicker
            testId={`additional-name-${name.rowKey}-effective-date`}
            label="Effective date"
            value={name.effectiveDate}
            error={fieldErrors[`additionalNames.${index}.effectiveDate`]}
            onValidChange={(value) => updateName(setDraft, name.rowKey, (current) => ({
              ...current,
              effectiveDate: value?.isValid() ? value.format('MM/DD/YYYY') : '',
            }))}
          />
        </Box>
        <Box sx={editFieldColumnSx}>
          <EditableDatePicker
            testId={`additional-name-${name.rowKey}-expiration-date`}
            label="Expiration date"
            value={name.expirationDate}
            error={fieldErrors[`additionalNames.${index}.expirationDate`]}
            onValidChange={(value) => updateName(setDraft, name.rowKey, (current) => ({
              ...current,
              expirationDate: value?.isValid() ? value.format('MM/DD/YYYY') : '',
            }))}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: 48, pt: 1 }}>
          <IconButton
            aria-label="Delete additional name"
            data-testid={`names-and-addresses-additional-name-${index}-delete`}
            onClick={() => deleteAdditionalName(setDraft, name)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>
      <Typography variant="subtitle2" sx={{ mt: 3 }}>
        Additional addresses
      </Typography>
      {name.addresses.map((address, addressIndex) => (
        <Box
          key={address.rowKey}
          sx={{
            mt: addressIndex === 0 ? 0 : 2,
            pt: addressIndex === 0 ? 0 : 2,
            borderTop: addressIndex === 0 ? 0 : '1px solid',
            borderColor: 'divider',
          }}
        >
          <AddressFields
            address={address}
            addressNumber={addressIndex + 1}
            fieldPrefix={`additionalNames.${index}.addresses.${addressIndex}`}
            stateLookups={stateLookups}
            fieldErrors={fieldErrors}
            onChange={(changes) =>
              updateAdditionalAddress(setDraft, name.rowKey, address.rowKey, changes)
            }
            onDelete={() => deleteAdditionalAddress(setDraft, name, address)}
          />
        </Box>
      ))}
      <Button
        variant="text"
        color="inherit"
        startIcon={<AddIcon />}
        onClick={() => addAdditionalAddress(setDraft, name)}
        aria-label="Add additional address"
        data-testid={`names-and-addresses-additional-name-${index}-add-address`}
      >
        Add address
      </Button>
    </Box>
  );
});

export function NamesAndAddressesEdit({
  draft,
  setDraft,
  stateLookups,
  fieldErrors,
}: Readonly<NamesAndAddressesEditProps>) {
  // Rows added via "Add additional name" this session default to the open edit form; rows already
  // present on mount default to the collapsed read-only summary (see AdditionalNameBlock).
  const newlyAddedRowKeysRef = useRef<Set<string>>(new Set());
  const addName = () => {
    const rowKey = `additional-new-${Date.now()}`;
    newlyAddedRowKeysRef.current.add(rowKey);
    setDraft((current) => ({
      ...current,
      additionalNames: [
        ...current.additionalNames,
        {
          rowKey,
          nameId: null,
          name: '',
          fein: '',
          effectiveDate: '',
          expirationDate: '',
          email: '',
          mailingAddress: null,
          addresses: [],
        },
      ],
    }));
  };
  return (
    <Box sx={{ p: 3 }} data-testid="names-and-addresses-edit">
      <Typography variant="h5">Primary names and addresses</Typography>
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 2 }}>
        Employer
      </Typography>
      <NameFields
        name={draft.primaryEmployer}
        prefix="primaryEmployer"
        required
        setDraft={setDraft}
        stateLookups={stateLookups}
        fieldErrors={fieldErrors}
      />
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>
        Agency
      </Typography>
      <NameFields
        name={draft.agency}
        prefix="agency"
        required
        setDraft={setDraft}
        stateLookups={stateLookups}
        fieldErrors={fieldErrors}
      />
      <Box sx={{ mt: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Additional names and addresses</Typography>
        <IconButton
          aria-label="Add additional name"
          data-testid="names-and-addresses-add-additional-name"
          onClick={addName}
        >
          <AddIcon />
        </IconButton>
      </Box>
      {draft.additionalNames.map((name, index) => (
        <AdditionalNameBlock
          key={name.rowKey}
          name={name}
          index={index}
          setDraft={setDraft}
          stateLookups={stateLookups}
          fieldErrors={fieldErrors}
          defaultEditing={newlyAddedRowKeysRef.current.has(name.rowKey)}
        />
      ))}
    </Box>
  );
}
